// version 4.1.0 2025-03-04
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';

import { isMobileOrTabletDevice } from 'axis-components';

@Component({
    host: { class: 'axis-menu-wrapper' },
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    standalone: false
})

export class MenuComponent implements OnDestroy, OnInit {

  /* properties */

  buttonDataKind = 'default'; // data-kind
  componentRef: any;
  el;
  elementIndex = 0;
  headingAvatar = false;
  headingDefault = true;
  headingIcon = false;
  id = '-' + (Math.random().toString(36).substr(2, 9) + performance.now().toString(36)).replace(/\./g, '');
  innerPanel;
  isMobileOrTabletDevice = false;
  keyClear;
  keysSoFar = '';
  menuCustom = false;
  menuLeft = 0;
  orderBy: any; // = [];
  settingsDefault = {
    align: 'left',
    arrowIcon: true,
    closeOnLeave: true,
    closeOnOutsideClick: false,
    columns: 1,
    columnsSubitems: 1,
    heading: '', // set text in button; requires showSelected: false
    headingAvatar: { color: '', image: '', initials: '', kind: '', name: '', src: '', size: '' },
    // name: is required; src: 'assets/images/avatars/name.png'
    headingIcon: { color: '', icon: '', font: '', size: '' },
    headingSelected: '',
    id: 'menuFlyout' + this.id, // unique to this menu
    itemClass: '', // space-delimited string of class names applied to all items
    items: [
      {
        flyoutVisible: false,
        itemClass: '', // space-delimited string of class names
        name: 'Item',
        nested: 'Nested Item',
        subitems: [
          { name: 'Subitem', value: 'subitem' },
        ],
        value: 'nested',
      },
    ],
    label: 'Menu Selection',
    menuVisible: false,
    orderBy: [], // +name/-name, +order1/-order1, +order2/-order2
    outline: 'none', // 'none': no border on menu button
    placeholder: 'Please select...', // when preselect is empty
    preselect: '', // unique value from items
    selected: { name: '', nested: '', value: '' }, // readonly object
    selectFlyout: false, // when true, select parent item of flyout (non-mobile/tablet only) on click
    scrollMenu: true,
    scrollSubitems: false,
    showOnHover: true,
    showSelected: true, // when true, show selected item in button
    showSelectedItem: false, // when true, highlight selected item in list
    templateClasses: '', // custom classes
    width: '', // full
    wrap: false, // false: don't wrap menu item text
  };
  subitemsCount = 0;

  @Input() axisIncludeComponent: any;
  @Input() public markup: any;
  @Input() public settings: any = {};

  @Output() selected = new EventEmitter();

  @ViewChild('menuContainer', { read: ViewContainerRef, static: true }) menuContainer: ViewContainerRef;
  @ViewChild('menuVar', { static: true }) menuContent: ElementRef;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
  ) {
    this.el = this.elRef.nativeElement;
    this.isMobileOrTabletDevice = isMobileOrTabletDevice();
  }

  /* lifecycle hooks */

  ngOnInit(): void {
    this.settings = { ...this.settingsDefault, ...this.settings };
    this.settings.headingAvatar = { ...this.settingsDefault.headingAvatar, ...this.settings.headingAvatar };
    this.settings.headingIcon = { ...this.settingsDefault.headingIcon, ...this.settings.headingIcon };
    this.settings.selected = { ...this.settingsDefault.selected, ...this.settings.selected };

    if (this.axisIncludeComponent) {

      this.componentRef = this.viewContainerRef.createComponent(this.axisIncludeComponent);

      this.componentRef.instance.componentRef = this;
      if (this.componentRef.instance.selected) {
        this.componentRef.instance.selected.subscribe(selection => this.selectItemCustom(selection));
      }
      this.menuContent.nativeElement.appendChild(
        this.componentRef.location.nativeElement
      );

      this.setCustomMenu();
    } else if (this.markup) {
      this.menuContent.nativeElement.innerHTML = this.markup;
      this.setCustomMenu();
    } else {
      this.buildItems(this.settings);
      this.orderBy = this.settings.orderBy;
    }

    if (this.settings.headingIcon.icon !== '') {
      this.headingDefault = false;
      this.headingIcon = true;
      if (this.settings.heading === '') {
        this.buttonDataKind = 'icon';
      } else {
        this.buttonDataKind = 'link';
      }
    } else if (this.settings.outline === 'none') {
      this.buttonDataKind = 'link';
    }

    if (this.settings.headingAvatar.name !== '') {
      this.headingDefault = false;
      this.headingAvatar = true;
    }

    if (this.isMobileOrTabletDevice) {
      this.settings.selectFlyout = false; // on non-desktop/laptop, only show flyout, can't show + select
    }

  }

  ngAfterViewInit(): void {
    this.setMenu();
  }

  ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  /* methods */

  @HostListener('window:resize', [])
  onResize(): void {
    this.setMenu();
  }

  buildItems(settings): void {
    const newItems = [];

    let badgeColor;
    let badgeHeading;
    let border;
    let heading;
    let headingSelected;
    let i = 0;
    let icon;
    let iconSelected;
    let itemClass;
    let labelColor;
    let labelHeading;
    let name;
    let nested;
    let newItem;
    let order1;
    let order2;
    let selected = { name: '', nested: '', value: '' };
    let selectedStatus;
    let size;
    let subitems;
    let value;

    for (const item of settings.items) {

      badgeColor = item.badgeColor || '';
      badgeHeading = item.badgeHeading || '';
      border = item.border || false;
      heading = item.heading || item.name;
      headingSelected = item.headingSelected || item.nameSelected;
      icon = item.icon || '';
      iconSelected = item.iconSelected || item.icon;
      itemClass = item.itemClass || '';
      labelColor = item.labelColor || '';
      labelHeading = item.labelHeading || '';
      name = item.name;
      order1 = item.order1 || '';
      order2 = item.order2 || '';
      nested = item.nested || '';
      selectedStatus = false;
      size = item.size || 'small';
      subitems = item.subitems || [];
      value = item.value;

      if (settings.itemClass !== '') {
        itemClass = itemClass + ' ' + settings.itemClass;
      }
      itemClass.trim();

      if (settings.preselect && settings.preselect === value) {
        selected = { name: name, nested: nested, value: value };
        selectedStatus = true;
      }

      if (subitems.length > 0 ) {
        let subitemCount = 0;
        for (const item of subitems) {
          item['id'] = 'si' + subitemCount; 
          subitemCount++
        }
      }

      newItem = {
        badgeColor: badgeColor,
        badgeHeading: badgeHeading,
        border: border,
        flyoutVisible: false,
        id: 'menu' + i + this.id,
        heading: heading,
        headingSelected: headingSelected,
        hover: false,
        icon: icon,
        iconSelected: iconSelected,
        itemClass: itemClass,
        labelColor: labelColor,
        labelHeading: labelHeading,
        name: name,
        nested: nested,
        order1: order1,
        order2: order2,
        selected: selectedStatus,
        size: size,
        value: value,
        subitems: subitems,
        subitemsCount: subitems.length,
      };
      this.subitemsCount = this.subitemsCount + subitems.length;

      newItems[i] = newItem;
      i++;
    }

    settings.items = newItems; // assign menu items
    settings.selected = selected; // preselect when available
    this.selected.emit(selected); // publish
  }

  clearKeysSoFarAfterDelay(): void {
    if (this.keyClear) {
      clearTimeout(this.keyClear);
      this.keyClear = null;
    }
    this.keyClear = setTimeout(() => {
      this.keysSoFar = '';
      this.keyClear = null;
    }, 500);
  }

  closeFlyout(action, event?): void {
    if (event) {
      this.stopPropagation(event);
    }
    if ((action === 'click' && this.settings.closeOnOutsideClick) ||
      (action === 'mouseleave' && this.settings.closeOnLeave)
    ) {
      this.settings.menuVisible = false;

      document.body.classList.remove('axis-dropdown-hidden-scroll');

    }
  }

  escapeMenu(optionEl, buttonEl): void {
    const buttonById = document.getElementById('button-' + this.settings.id);

    if (buttonById === document.activeElement) { // escape from main button
      this.elementIndex = 0; // reset for return
      this.settings.menuVisible = false;
    } else if (optionEl.getAttribute('data-flyout') === 'option') { // escape from main item
      buttonEl.focus();
      this.elementIndex = 0; // reset for return
      this.settings.menuVisible = false;
    } else {
      this.escapeSubmenu(optionEl); // escape from nested item (move up)
    }
  }

  escapeSubmenu(optionEl): void {
    const parentEl = optionEl.parentNode.parentNode.parentNode;
    parentEl.focus();
    this.elementIndex = Number(parentEl.getAttribute('data-index'));
  }

  focusActiveOption(settings: any): void {

    if (this.isMobileOrTabletDevice) {
      settings.closeOnOutsideClick = true
    }

    if (settings.showOnHover && !this.isMobileOrTabletDevice) {
      settings.menuVisible = true;
    }

    // set hoverable, click-to-close overlay to keep menu open when moving from button to menu, menu to flyout
    const buttonEl = document.querySelector<HTMLElement>('#button-' + this.settings.id);
    const buttonHeight = buttonEl.getBoundingClientRect().height;

    const hoverable = document.getElementById('axis-menu-hoverable-' + this.settings.id);
    const hoverableHeight = buttonHeight + 20; // 20 = flyout padding
    hoverable.style.top = '-' + buttonHeight + 'px';
    hoverable.style.height = 'calc(100% + ' + hoverableHeight + 'px)';

    if (this.settings.width === 'full') {
      const menuParent = document.getElementById(this.settings.id);
      const menuLeft = menuParent.getBoundingClientRect().left;
      this.menuLeft = -menuLeft;
    }

    this.setMaxHeight();

  }

  focusOption(event: any): void {
    let navigateVertical = true;
    const key = event.key.toLocaleLowerCase();
    const optionEl = event.target;
    let listboxEl;
    let buttonEl;
    let optionElements;

    // data-flyout identifies valid keyboard-accessible elements (excludes .axis-menu-mobile elements):
    if (this.menuCustom) {
      listboxEl = document.querySelector<HTMLElement>('#' + this.settings.id);
      buttonEl = document.querySelector<HTMLElement>('#button-' + this.settings.id);
      optionElements = listboxEl.querySelectorAll('[data-flyout]');
    } else {
      listboxEl = optionEl.parentNode.parentNode;
      buttonEl = this.el.querySelector('#' + listboxEl.getAttribute('aria-labelledby'));
      optionElements = listboxEl.querySelectorAll('[data-flyout="' + optionEl.getAttribute('data-flyout') + '"]');
    }

    switch (key) {
      case 'arrowdown':
      case 'down':
      case 'pagedown':
        event.preventDefault();
        this.getElement(1, optionElements);
        break;
      case 'arrowleft':
        event.preventDefault();
        navigateVertical = false;
        this.escapeSubmenu(optionEl);
        break;
      case 'arrowright':
        event.preventDefault();
        navigateVertical = false;
        this.goSubmenu(optionEl);
        break;
      case 'arrowup':
      case 'pageup':
      case 'up':
        event.preventDefault();
        this.getElement(-1, optionElements);
        break;
      case 'end':
        event.preventDefault();
        this.elementIndex = optionElements.length - 1;
        break;
      case ' ':
      case 'enter':
        event.preventDefault();
        if (optionEl.getAttribute('aria-haspopup') === 'true') {
          navigateVertical = false;
          this.goSubmenu(optionEl);
        } else {
          optionEl.click(); // invoke routerLink or click
          setTimeout(() => {
            buttonEl.focus(); // focus menu button after routerLink
            this.elementIndex = 0; // reset for return
            this.settings.menuVisible = false;
            listboxEl.setAttribute('aria-activedescendant', event.target.id);
          }, 200);
        }
        break;
      case 'esc':
      case 'escape':
        event.preventDefault();
        navigateVertical = false;
        this.escapeMenu(optionEl, buttonEl);
        break;
      case 'home':
        event.preventDefault();
        this.elementIndex = 0;
        break;
      case 'first':
        this.elementIndex = 0;
        break;
      case 'id':
        event.preventDefault();
        document.querySelector<HTMLElement>('#' + event.target.id).focus();
        break;
      case 'shift':
        navigateVertical = false;
        break;
      case 'tab':
        navigateVertical = false;
        this.elementIndex = 0; // reset for return
        this.settings.menuVisible = false;
        for (const item of this.settings.items) {
          item.flyoutVisible = false;
        }
        break;
      default: // search option titles for matching character
        navigateVertical = false;
        if (/[a-z0-9\/a()]/.test(key)) {
          this.keysSoFar = this.keysSoFar + event.key.toLocaleLowerCase();
          this.clearKeysSoFarAfterDelay();
          const searchChar = this.keysSoFar;
          const ol = optionElements.length - 1;
          let label;

          for (let index = ol; index >= 0; index--) {
            label = optionElements[index].innerText;
            if (label && label.toLocaleLowerCase().indexOf(searchChar) === 0) {
              optionElements[index].focus();
            }
          }
        }
        break;
    }
    if (optionElements[this.elementIndex] && navigateVertical) {
      optionElements[this.elementIndex].focus();
      this.stopPropagation(event); // only scroll current level
    }

  }

  getElement(step, optionElements) {
    let index = this.elementIndex;
    index += step;

    if (index < -1) { // out of range, at top of menu
      // console.log('   ...option '+index+' is out of range');
      index = 0;
    } else if (index < 0) { // out of range
      // console.log('   ...option '+index+' is out of range');
      return;
    } else if (index >= optionElements.length) { // out of range, at bottom of menu
      // console.log('   ...option '+index+' is out of range');
      index = optionElements.length - 1;
    }
    this.elementIndex = index;
  }

  goMenu(event): void {
    event.preventDefault();
    if (this.el.querySelector('#menu-' + this.settings.id)) {
      this.el.querySelector('#menu-' + this.settings.id + ' a:not(.selected)').focus();
    } else {
      console.error('Element with id #menu-' + this.settings.id + ' not found.');
    }
  }

  goSubmenu(optionEl): void {
    const optionElements = optionEl.querySelectorAll('[data-flyout="suboption"]');
    setTimeout(() => {
      this.elementIndex = 0;
      if (optionElements[this.elementIndex]) {
        optionElements[this.elementIndex].focus();
      }
    }, 200);
  }

  mouseItem(item, level, status, itemVar?): void {
    if (this.settings.showOnHover) {
      itemVar = itemVar ? itemVar : null;
      this.showItem(item, level, status, itemVar);
    }
  }

  openMenu(): void {
    this.settings.menuVisible = true;
    this.setMenu();
  }

  selectItem(itemSelected, event?): void {
    this.stopPropagation(event);
    if (itemSelected.subitemsCount && itemSelected.subitemsCount > 0 && !this.settings.selectFlyout) { return; }

    const selectedObj = { name: itemSelected.name, nested: itemSelected.nested, value: itemSelected.value };

    this.settings.selected = selectedObj;
    itemSelected.flyoutVisible = false;
    itemSelected.selected = !itemSelected.selected;
    this.settings.menuVisible = false;

    for (const item of this.settings.items) { // ensure flyouts collapse on selection
      item.flyoutVisible = false;
      for (const subitem of item.subitems) {
        subitem.flyoutVisible = false;
      }
    }

    this.selected.emit(selectedObj);
  }

  selectItemCustom(itemSelected): void {
    const selectedObj = { name: itemSelected.name, nested: itemSelected.nested || '', value: itemSelected.value || '' };

    this.settings.selected = selectedObj;
    this.settings.menuVisible = false;

    this.selected.emit(itemSelected);
  }

  selectOrShow(item, level, event): void {
    if (item.subitems && item.subitemsCount > 0 && !this.settings.showOnHover && !this.settings.selectFlyout) {
      for (const item of this.settings.items) { // ensure flyouts collapse on selection
        item.flyoutVisible = false;
        for (const subitem of item.subitems) {
          subitem.flyoutVisible = false;
        }
      }
      const flyoutVisible = !item.flyoutVisible;
      this.showItem(item, level, flyoutVisible);
    } else {
      this.selectItem(item, event);
    }
  }

  setCustomMenu(): void { // override possible settings that disrupt the custom menu option
    this.menuCustom = true;
    this.settings.columns = 1;
  }

  setMaxHeight(): void {
    if (this.subitemsCount > 0) { return; } // setting max height hides flyout
    // if (!this.settings.scrollMenu) { return; }
    setTimeout(() => {
      if (this.settings.menuVisible) { // menu is open
        document.getElementById('menu-' + this.settings.id).style.height = null; // reset
        document.getElementById('menu-' + this.settings.id).style.overflowY = null; // reset
        const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        const buttonEl = document.getElementById('menu-' + this.settings.id); // get menu position
        const buttonTop = buttonEl.getBoundingClientRect().top;
        const buttonElMenuHeight = document.getElementById('menu-' + this.settings.id).getBoundingClientRect().height;
        const excessHeight = vh - (buttonTop + buttonElMenuHeight); // if negative result, too high to fit
        if (excessHeight < 0) {
          const newHeight = buttonElMenuHeight + excessHeight - 5;
          document.getElementById('menu-' + this.settings.id).style.height = newHeight + 'px';
          document.getElementById('menu-' + this.settings.id).style.overflowY = 'scroll';

          document.body.classList.add('axis-dropdown-hidden-scroll');

        }
      }
    });
  }

  setMenu(): void {

    if (document.getElementById(this.settings.id) && this.settings.columns > 1 && this.settings.width !== 'full') {
      const buttonEl = document.getElementById(this.settings.id); // get button position
      const buttonLeft = buttonEl.getBoundingClientRect().left;
      const buttonRight = buttonEl.getBoundingClientRect().right;
      const buttonWidth = buttonEl.getBoundingClientRect().width;

      var buttonElHidden = buttonEl.querySelector('.axis-menu'); // get menu width
      buttonElHidden.setAttribute('style', 'display:block;opacity:0');
      const buttonElMenuWidth = document.getElementById('menu-' + this.settings.id).getBoundingClientRect().width;
      buttonElHidden.removeAttribute('style');

      const leftRightSpacing = 64; // set gap for menu from left or right edge of viewport
      const vw = window.innerWidth; // get viewport size

      if (this.settings.align === 'right') {

        if (buttonRight > buttonElMenuWidth)
          this.settings.containerWidth = buttonElMenuWidth
        else
          this.settings.containerWidth = buttonRight - leftRightSpacing;

      } else {
        if (vw - buttonLeft > buttonElMenuWidth)
          this.settings.containerWidth = buttonElMenuWidth
        else
          this.settings.containerWidth = vw - buttonLeft - leftRightSpacing;
      }
      this.changeDetectorRef.detectChanges();

    } else if (document.getElementById(this.settings.id)) {
      this.setMaxHeight();
    }

  }

  show(trv): void {
    console.log(trv); // this console.log doesn't need to be removed
  }

  showItem(item, level, status, itemVar?): void {
    switch (level) {
      case 1: // showOnEnter(item, 1, true/false, itemVar)
        if (itemVar) {
          itemVar.blur();
        }
        item.hover = status;
        break;
      case 2: // showOnEnter(item, 2, true/false)
        item.flyoutVisible = status;
        item.hover = status;
        break;
      case 3: // showOnEnter(item, 3, true/false)
        item.flyoutVisible = status;
        break;
    }
  }

  stopPropagation(event): void {
    if (event.target) {
      event.stopPropagation();
    }
  }
}
